import { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeftRight, AlertCircle, Zap } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useCurrency } from '@/contexts';
import { CustomSelect } from '@/components/ui/custom-select';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/lib/database.types';

type Account = Database['public']['Tables']['accounts']['Row'];

interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  fromAccountId?: string;
  toAccountId?: string;
  defaultFromAccountId?: string; // Alias for convenience
}

// Quick transfer amounts
const QUICK_AMOUNTS = [50, 100, 200, 500, 1000];

export function TransferModal({
  isOpen,
  onClose,
  onSuccess,
  fromAccountId,
  toAccountId,
  defaultFromAccountId,
}: TransferModalProps) {
  const { t } = useTranslation();
  const { formatAmount, formatAmountInCurrency } = useCurrency();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    fromAccountId: fromAccountId || defaultFromAccountId || '',
    toAccountId: toAccountId || '',
  });

  const fromAccount = accounts.find((a) => a.id === formData.fromAccountId);
  const toAccount = accounts.find((a) => a.id === formData.toAccountId);
  const amount = parseFloat(formData.amount || '0');
  const insufficientBalance = fromAccount && amount > fromAccount.balance;

  useEffect(() => {
    if (isOpen) {
      loadAccounts();
    }
  }, [isOpen]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      fromAccountId: fromAccountId || defaultFromAccountId || prev.fromAccountId,
      toAccountId: toAccountId || prev.toAccountId,
    }));
  }, [fromAccountId, toAccountId, defaultFromAccountId]);

  const loadAccounts = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('accounts')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_hidden', false)
      .order('name');

    if (error) {
      console.error('Error loading accounts:', error);
      return;
    }

    setAccounts(data || []);

    // Auto-select first account if none selected
    if (!fromAccountId && data && data.length > 0) {
      setFormData((prev) => ({
        ...prev,
        fromAccountId: prev.fromAccountId || data[0].id,
        toAccountId: prev.toAccountId || (data.length > 1 ? data[1].id : ''),
      }));
    }
  };

  const handleSwapAccounts = () => {
    setFormData((prev) => ({
      ...prev,
      fromAccountId: prev.toAccountId,
      toAccountId: prev.fromAccountId,
    }));
  };

  const handleQuickAmount = (quickAmount: number) => {
    setFormData((prev) => ({ ...prev, amount: quickAmount.toString() }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!formData.fromAccountId || !formData.toAccountId) {
      alert(t('transfer.selectBothAccounts'));
      return;
    }

    if (formData.fromAccountId === formData.toAccountId) {
      alert(t('transfer.sameAccountError'));
      return;
    }

    if (insufficientBalance) {
      alert(t('transfer.insufficientBalance'));
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.from('transactions').insert([
        {
          amount,
          description:
            formData.description ||
            `${t('transfer.defaultDescription')} ${fromAccount?.name} → ${toAccount?.name}`,
          date: new Date().toISOString().split('T')[0],
          type: 'transfer',
          account_id: formData.fromAccountId,
          to_account_id: formData.toAccountId,
          user_id: user.id,
        },
      ]);

      if (error) throw error;

      await onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error creating transfer:', error);
      alert('Error creating transfer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      description: '',
      fromAccountId: fromAccountId || '',
      toAccountId: toAccountId || '',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700/50 bg-gradient-to-r from-blue-500 to-indigo-600">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <ArrowLeftRight className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">{t('transfer.title')}</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Account Selection */}
          <div className="space-y-3">
            {/* From Account */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('transfer.from')}
              </label>
              <CustomSelect
                required
                value={formData.fromAccountId}
                onChange={(value) => setFormData({ ...formData, fromAccountId: value })}
                placeholder={t('transfer.selectAccount')}
                options={accounts.map((account) => ({
                  value: account.id,
                  label: `${account.name} (${formatAmountInCurrency(account.balance, account.currency)})`,
                }))}
              />
              {fromAccount && (
                <p
                  className={`text-xs mt-1 ${insufficientBalance ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}
                >
                  {t('transfer.available')}:{' '}
                  {formatAmountInCurrency(fromAccount.balance, fromAccount.currency)}
                </p>
              )}
            </div>

            {/* Swap Button */}
            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleSwapAccounts}
                className="w-10 h-10 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors group"
                title={t('transfer.swap')}
              >
                <ArrowLeftRight className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-blue-500 transition-colors" />
              </button>
            </div>

            {/* To Account */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('transfer.to')}
              </label>
              <CustomSelect
                required
                value={formData.toAccountId}
                onChange={(value) => setFormData({ ...formData, toAccountId: value })}
                placeholder={t('transfer.selectAccount')}
                options={accounts
                  .filter((a) => a.id !== formData.fromAccountId)
                  .map((account) => ({
                    value: account.id,
                    label: `${account.name} (${formatAmountInCurrency(account.balance, account.currency)})`,
                  }))}
              />
              {toAccount && amount > 0 && (
                <p className="text-xs text-green-500 mt-1">
                  {t('transfer.newBalance')}:{' '}
                  {formatAmountInCurrency(toAccount.balance + amount, toAccount.currency)}
                </p>
              )}
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('transfer.amount')}
            </label>
            <input
              type="text"
              inputMode="decimal"
              required
              value={formData.amount}
              onChange={(e) => {
                // Allow digits, dots, and commas for decimal input
                const value = e.target.value.replace(',', '.');
                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                  setFormData({ ...formData, amount: value });
                }
              }}
              className={`w-full px-4 py-4 bg-gray-50 dark:bg-gray-700 border rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 text-2xl font-bold text-center ${
                insufficientBalance
                  ? 'border-red-500 focus:ring-red-500/30'
                  : 'border-gray-200 dark:border-gray-600 focus:ring-blue-500/30 focus:border-blue-500'
              }`}
              placeholder="0.00"
            />
            {insufficientBalance && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {t('transfer.insufficientBalance')}
              </p>
            )}
          </div>

          {/* Quick Amounts */}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
              <Zap className="w-3 h-3" />
              {t('transfer.quickAmounts')}
            </label>
            <div className="flex flex-wrap gap-2">
              {QUICK_AMOUNTS.map((quickAmount) => (
                <button
                  key={quickAmount}
                  type="button"
                  onClick={() => handleQuickAmount(quickAmount)}
                  disabled={fromAccount && quickAmount > fromAccount.balance}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    parseFloat(formData.amount) === quickAmount
                      ? 'bg-blue-500 text-white'
                      : fromAccount && quickAmount > fromAccount.balance
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {formatAmount(quickAmount)}
                </button>
              ))}
              {fromAccount && (
                <button
                  type="button"
                  onClick={() => handleQuickAmount(fromAccount.balance)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    parseFloat(formData.amount) === fromAccount.balance
                      ? 'bg-blue-500 text-white'
                      : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                  }`}
                >
                  {t('transfer.all')}
                </button>
              )}
            </div>
          </div>

          {/* Description (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('transfer.description')}{' '}
              <span className="text-gray-400">({t('common.optional')})</span>
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              placeholder={t('transfer.descriptionPlaceholder')}
            />
          </div>

          {/* Transfer Summary */}
          {fromAccount && toAccount && amount > 0 && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {fromAccount.name}
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {formatAmountInCurrency(fromAccount.balance, fromAccount.currency)}
                  </p>
                  <p className="text-xs text-red-500">
                    -{formatAmountInCurrency(amount, fromAccount.currency)}
                  </p>
                </div>

                <div className="px-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <ArrowRight className="w-4 h-4 text-white" />
                  </div>
                </div>

                <div className="flex-1 text-right">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{toAccount.name}</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {formatAmountInCurrency(toAccount.balance, toAccount.currency)}
                  </p>
                  <p className="text-xs text-green-500">
                    +{formatAmountInCurrency(amount, toAccount.currency)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || insufficientBalance || !amount}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <ArrowLeftRight className="w-4 h-4" />
                  {t('transfer.submit')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
